// PR-DVA MAY 11 — driver↔vehicle reassignment sheet.
// Used from VehiclesPage edit drawer (change the vehicle's driver) and from
// DriversPage edit drawer (change the driver's vehicle). The pickable list
// loads the OTHER side filtered to active, with a "currently on X" warning
// for picks that would close another active assignment.
import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface PickerItem {
  id: string;
  label: string;
  /** Optional flag — true if this pick currently has another active assignment. */
  hasActive?: boolean;
  /** Optional warning text when hasActive is true. */
  activeOn?: string;
}

export interface AssignmentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Assign driver to VEH-0002" or "Assign vehicle to DRV-0007" */
  title: string;
  description?: string;
  /** "driver" or "vehicle" — the side we're picking. */
  pickKind: 'driver' | 'vehicle';
  /** Items to render in the picker. */
  options: PickerItem[];
  /** Body to send to POST /driver-vehicle-assignments. The picked id is folded in. */
  buildPayload: (pickedId: string, args: { effectiveFrom: string; reason: string }) => Record<string, unknown>;
  onSaved?: () => void;
}

export function AssignmentSheet({
  open, onOpenChange, title, description,
  pickKind, options, buildPayload, onSaved,
}: AssignmentSheetProps) {
  const [pickedId, setPickedId] = useState<string>('');
  const [effectiveFrom, setEffectiveFrom] = useState<string>(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setPickedId(''); setReason(''); setError(null); setWarnings([]);
      setEffectiveFrom(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const picked = options.find((o) => o.id === pickedId);

  async function save() {
    if (!pickedId) { setError(`Pick a ${pickKind}`); return; }
    setSaving(true); setError(null); setWarnings([]);
    try {
      const r = await apiClient.post<{ id: string; code: string; warnings?: string[] }>(
        '/api/v1/driver-vehicle-assignments',
        buildPayload(pickedId, { effectiveFrom, reason: reason.trim() }),
      );
      setWarnings(r.data.warnings ?? []);
      onSaved?.();
      // Close after a short pause so warnings render briefly
      setTimeout(() => onOpenChange(false), 800);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
          <div className="space-y-1">
            <Label>New {pickKind} *</Label>
            <select
              value={pickedId}
              onChange={(e) => setPickedId(e.target.value)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base"
            >
              <option value="">— Select —</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.label}{o.hasActive ? ' · currently on ' + (o.activeOn ?? 'another') : ''}</option>
              ))}
            </select>
            {picked?.hasActive && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Selecting will close their current assignment on <strong>{picked.activeOn}</strong>.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Effective from *</Label>
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Backdated assignments do not retroactively change old trip attribution.</p>
          </div>
          <div className="space-y-1">
            <Label>Reason</Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Why this change?"
            />
          </div>
          {error && (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">
              {error}
            </div>
          )}
          {warnings.length > 0 && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 p-3 text-xs space-y-1">
              {warnings.map((w) => <div key={w}>· {w}</div>)}
            </div>
          )}
        </div>
        <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !pickedId}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Assign
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
